import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DrawerComponent } from 'src/components/DialogComponent';
import Loading from 'src/components/Loading';
import TGButton from 'src/components/ui/TGButton';
import { useAppSelector } from 'src/hooks/redux';
import { selectCurrentUser } from 'src/modules/users/selector';
import { FlexCol, FlexRow } from 'src/utils/flexLayouts';
import { getAndroidSafeAreaTopPaddingCss, isAndroidWebView } from 'src/utils/mobile';
import Add5SAuditMobile, { Add5SAuditMobileHandle } from '../add5SAudit/add5SAudit.mobile';
import { addStrapiData, deleteStrapiData, extractStrapiData, fetchOne5SCardById, updateStrapiData } from '../api/api';
import { hasLocationAccess } from '../utils/locationAccess';
import PassAudit5SMobile, { PassAudit5SMobileHandle } from './PassAudit5S.mobile';

interface Props {
  open: boolean;
  card?: any;
  cardId?: number | null;
  onClose: () => void;
  zIndex?: number;
}

export default function PassAudit5SScreenMobile({ open, card, cardId, onClose, zIndex = 50 }: Props) {
  const currentUser: any = useAppSelector(selectCurrentUser);

  const headerInsetStyle = useMemo(() => {
    if (!isAndroidWebView()) return undefined;
    const inset = getAndroidSafeAreaTopPaddingCss(0);
    return { top: `calc(-1 * ${inset})`, marginTop: `calc(-1 * ${inset})`, paddingTop: `calc(16px + ${inset})` };
  }, []);
  const [loadedCard, setLoadedCard] = useState<any>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const [percent, setPercent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [draftCard, setDraftCard] = useState<{ id: number; documentId: string } | null>(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [prevAuditId, setPrevAuditId] = useState<number | null>(null);
  const addRef = useRef<Add5SAuditMobileHandle | null>(null);
  const questionsRef = useRef<PassAudit5SMobileHandle | null>(null);

  const draftCreatedRef = useRef(false);

  const isExisting = !!card?.id || !!cardId;
  const effectiveCard = card?.id ? card : loadedCard;

  const readOnly = !!(effectiveCard?.Location?.id && !hasLocationAccess(currentUser, effectiveCard.Location.id));

  useEffect(() => {
    if (!open || !cardId || card?.id) return;
    let cancelled = false;
    setCardLoading(true);
    setLoadedCard(null);
    fetchOne5SCardById(cardId)
      .then((resp) => {
        if (!cancelled) setLoadedCard(extractStrapiData(resp)?.[0] || null);
      })
      .catch((e) => console.error('Не удалось загрузить аудит 5S', e))
      .finally(() => {
        if (!cancelled) setCardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, cardId, card]);

  useEffect(() => {
    if (!open) return;
    if (!effectiveCard?.id) return;
    setDraftCard({ id: effectiveCard.id, documentId: effectiveCard.documentId });
    draftCreatedRef.current = false;
  }, [open, effectiveCard]);

  const cardPlaceholder = useMemo(() => {
    if (effectiveCard?.id) return effectiveCard;
    if (!formValues) return null;
    return {
      id: draftCard?.id,
      documentId: draftCard?.documentId,
      TypeAudit: formValues.typeAudit?.name || null,
      Location: formValues.location ? { id: formValues.location.id } : null,
      Department: formValues.department ? { id: formValues.department.id } : null,
      Area: formValues.area ? { id: formValues.area.id } : null,
      Zone: formValues.zone?.name || formValues.zone || null,
    };
  }, [effectiveCard, formValues, draftCard]);

  useEffect(() => {
    if (!open) return;
    if (isExisting) return;
    if (draftCard) return;
    if (draftSaving) return;
    if (!formValues) return;

    const v = formValues;
    const isCross = v.typeAudit?.id === 2;
    const filled = !!(
      v.dateAudit?.startDate &&
      v.typeAudit?.id &&
      v.location?.id &&
      v.department?.id &&
      v.area?.id &&
      (isCross || v.zone?.id) &&
      v.auditor?.id
    );
    if (!filled) return;

    setDraftSaving(true);
    const titleParts = [
      v.dateAudit?.startDate,
      v.location?.name,
      v.area?.name,
      v.zone?.name,
      v.typeAudit?.name,
    ].filter(Boolean);
    const draftBody = {
      Title: titleParts.join(' · '),
      DateAudit: v.dateAudit.startDate,
      PlannedDate: v.dateAudit.startDate,
      TypeAudit: v.typeAudit.name,
      Location: v.location.id,
      Department: v.department.id,
      Area: v.area.id,
      Zone: isCross ? null : v.zone?.name,
      Auditor: v.auditor.id,
      CoAuditor: v.soauditor?.id || null,
      Comment: v.comment || null,
    };

    addStrapiData('mt-5-s-cards', draftBody)
      .then((resp: any) => {
        const data = resp?.data?.data;
        if (data) {
          draftCreatedRef.current = true;
          setDraftCard({ id: data.id, documentId: data.documentId });
        }
      })
      .catch((e) => console.error('Ошибка создания черновика 5S', e))
      .finally(() => setDraftSaving(false));
  }, [open, isExisting, formValues, draftCard, draftSaving]);

  const resetState = useCallback(() => {
    setFormValues(null);
    setPercent(0);
    setDraftCard(null);
    setLoadedCard(null);
    draftCreatedRef.current = false;
    onClose();
  }, [onClose]);

  const closeDialog = useCallback(async () => {
    if (draftCard && draftCreatedRef.current) {
      try {
        await deleteStrapiData('mt-5-s-cards', draftCard.documentId);
      } catch (e) {
        console.error('Не удалось удалить черновик 5S', e);
      }
    }
    resetState();
  }, [draftCard, resetState]);

  const handleSave = useCallback(async (cardBody: any) => {
    if (saving || readOnly) return;
    setSaving(true);
    try {
      if (draftCard) {
        await questionsRef.current?.saveAnswers?.();
        await updateStrapiData('mt-5-s-cards', draftCard.documentId, cardBody);
      } else {
        const cardResp: any = await addStrapiData('mt-5-s-cards', cardBody);
        const newId = cardResp?.data?.data?.id;
        const answers = questionsRef.current?.getAnswers() || [];
        if (newId && answers.length > 0) {
          await Promise.all(
            answers.map((a) => addStrapiData('mt-5-s-card-answers', { Cards: newId, ...a }))
          );
        }
      }
      resetState();
    } catch (e) {
      console.error('Ошибка сохранения 5S аудита', e);
    } finally {
      setSaving(false);
    }
  }, [saving, readOnly, draftCard, resetState]);

  const waitingCard = !!cardId && !card?.id && !loadedCard;

  return (
    <DrawerComponent open={open} zIndex={zIndex} toggleDrawer={closeDialog}>
      <FlexCol className='grow'>
        <FlexRow
          className='items-start justify-between px-4 py-4 border-b bg-white sticky top-0 z-[110]'
          style={headerInsetStyle}
        >
          <FlexCol>
            <span className='text-[16px] text-black'>Проведение аудита</span>
            <span className='text-sm text-[#637381]'>{percent}%</span>
          </FlexCol>
          <button type='button' onClick={closeDialog} className='p-1 text-[#637381]'>
            <XMarkIcon className='w-6 h-6' />
          </button>
        </FlexRow>

        <FlexCol className='grow px-4 py-5 gap-6'>
          {waitingCard && cardLoading && (
            <FlexCol className='items-center justify-center py-20'>
              <Loading />
            </FlexCol>
          )}

          {waitingCard && !cardLoading && (
            <p className='text-sm text-center text-[#637381] py-10'>Аудит не найден</p>
          )}

          {open && !waitingCard && (
            <Add5SAuditMobile
              ref={addRef}
              key={effectiveCard?.id || 'new'}
              editData={effectiveCard}
              readOnly={readOnly}
              onSubmitOverride={handleSave}
              onValuesChange={setFormValues}
            />
          )}

          {open && !waitingCard && cardPlaceholder && draftCard && (
            <PassAudit5SMobile
              ref={questionsRef}
              key={`pass-${draftCard.id}-${cardPlaceholder.TypeAudit || ''}-${cardPlaceholder.Location?.id || ''}-${cardPlaceholder.Department?.id || ''}-${cardPlaceholder.Area?.id || ''}-${cardPlaceholder.Zone || ''}`}
              card={cardPlaceholder}
              onPercentChange={setPercent}
              onOpenPrevAudit={setPrevAuditId}
              readOnly={readOnly}
            />
          )}

          {open && !isExisting && !draftCard && !draftSaving && (
            <div className='text-center text-sm text-[#637381] py-6'>
              Заполните все обязательные поля шапки, чтобы продолжить
            </div>
          )}
        </FlexCol>

        <FlexCol className='gap-3 px-4 py-4 border-t bg-white sticky bottom-0'>
          <TGButton
            type='button'
            color='bluedark'
            className='w-full'
            onClick={() => addRef.current?.submit()}
            disabled={saving || !draftCard || readOnly}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </TGButton>
          <TGButton type='button' className='w-full' onClick={closeDialog}>
            Отмена
          </TGButton>
        </FlexCol>
      </FlexCol>

      {prevAuditId && (
        <PassAudit5SScreenMobile
          open
          cardId={prevAuditId}
          onClose={() => setPrevAuditId(null)}
          zIndex={zIndex + 10}
        />
      )}
    </DrawerComponent>
  );
}
